import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import React, { isValidElement as isValidElement$1, Fragment, Children } from 'react';
import { ErrorConstant } from '@remirror/core-constants';
import { isObject, isString, bool, isFunction, invariant } from '@remirror/core-helpers';

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

/**
 * These are the constants used to determine whether an element is a remirror constant.
 */
var RemirrorType;
/**
 * A drop in replacement for built in React.isValidElement which accepts a test value of any type
 *
 * @param value - the value to check
 */

(function (RemirrorType) {
  RemirrorType["SSR"] = "ssr";
  RemirrorType["Editor"] = "editor";
  RemirrorType["Provider"] = "provider";
  RemirrorType["ContextProvider"] = "context-provider";
  RemirrorType["I18nProvider"] = "i18n-provider";
  RemirrorType["ThemeProvider"] = "theme-provider";
})(RemirrorType || (RemirrorType = {}));

function isValidElement(value) {
  return isObject(value) && /*#__PURE__*/isValidElement$1(value);
}
/**
 * Check whether a react node is a built in dom element (i.e. `div`, `span`)
 *
 * @param value - the value to check
 */

function isReactDOMElement(value) {
  return isValidElement(value) && isString(value.type);
}
/**
 * Checks whether the element is a react fragment
 *
 * @param value - the value to check
 */

function isReactFragment(value) {
  return isObject(value) && isValidElement(value) && value.type === Fragment;
}
/**
 * Retrieve the element props for JSX Element
 *
 * @param element
 */

function getElementProps(element) {
  return isValidElement(element) ? element.props : {};
}
/**
 * Utility for properly typechecking static defaultProps for a class component in react.
 *
 * ```ts
 * static defaultProps = asDefaultProps<RemirrorProps>()({
 *   initialContent: EMPTY_PARAGRAPH_NODE,
 * });
 * ```
 */

var asDefaultProps = () => props => props;
/**
 * Checks if this element has a type of any RemirrorComponent
 *
 * @param value - the value to check
 */

var isRemirrorElement = value => {
  return bool(isObject(value) && isValidElement(value) && value.type.$$remirrorType);
};

var isRemirrorElementOfType = type => value => isRemirrorElement(value) && value.type.$$remirrorType === type;
/**
 * Checks to see if this is the wrapper we've created around the RemirrorContent.Provider component.
 *
 * This is used to help determine how the Remirror component will be rendered. `getRootProps` is the main reason
 * for this, and I'm not even sure the effort is worth it.
 *
 * @param value - the value to check
 */


var isRemirrorContextProvider = /*#__PURE__*/isRemirrorElementOfType(RemirrorType.ContextProvider);
/**
 * Finds if this is a RemirrorProvider (which provides the RemirrorInjectedProps into the context);
 *
 * @param value - the value to check
 */

var isRemirrorProvider = /*#__PURE__*/isRemirrorElementOfType(RemirrorType.Provider);
/**
 * Will throw an error if the child provided is not a function.
 *
 * @remarks
 * This is currently used in the remirror component to throw an error when the element children
 * are not a render prop. It should be called outside of render for class Components.
 *
 * @param prop - the prop to test
 */

var propIsFunction = value => {
  !isFunction(value) ? process.env.NODE_ENV !== "production" ? invariant(false, {
    code: ErrorConstant.INTERNAL,
    message: 'The child argument to the Remirror component must be a function.'
  }) : invariant(false) : void 0;
  return true;
};
/**
 * A drop in replacement for React.Children.only which provides more readable errors
 * when the child is not a react element or undefined.
 */

var oneChildOnly = value => {
  if (!value) {
    throw new Error('This component requires ONE child component - Nothing was provided');
  }

  if (!isValidElement(value)) {
    throw new Error('This component requires ONE child component - An invalid element was provided');
  }

  return Children.only(value);
};
/**
 * Add the specified key to an element when it is a valid react element.
 *
 * This is useful when returning an array of components because a fragment isn't sufficient.
 */

var addKeyToElement = (element, key) => {
  if (!isValidElement(element)) {
    return element;
  }

  return /*#__PURE__*/React.cloneElement(element, _objectSpread(_objectSpread({}, element.props), {}, {
    key
  }));
};

export { RemirrorType, addKeyToElement, asDefaultProps, getElementProps, isReactDOMElement, isReactFragment, isRemirrorContextProvider, isRemirrorElement, isRemirrorProvider, isValidElement, oneChildOnly, propIsFunction };
